﻿import React, { useState, useEffect } from 'react';
import { XMasonry, XBlock } from 'react-xmasonry';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import { Card, Box, Typography, Skeleton, List, useMediaQuery } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import ArticleIcon from '@mui/icons-material/Article';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import SharepointLink from 'Components/Global/SharepointLink/SharepointLink';
import Grid from '@mui/material/Grid';
import LoadingIndicator from 'Components/Global/loading-indicator/loading-indicator';
import './Brokerage.scss';
import {
  GetBrokerageProducts,
  GetBrokerageCategories,
  GetBrokerageHeaderItems,
} from 'api/brokerage.js';
import { useQuery } from '@tanstack/react-query';
import { fetchSignedURL } from '../../api/file';
import { useLinkTypes } from '../../hooks/useLinkTypes';
import CircularProgress from '@mui/material/CircularProgress';

function Brokerage() {
  const linkTypeQuery = useLinkTypes();

  const handleSecuredFile = async (path) => {
    await fetchSignedURL(path);
  };

  const productIconStyles = {
    marginRight: '.2rem',
    fontSize: '1rem !important',
  };

  const DisplayProduct = (p, showTitle) => (
    <div>
      <List sx={{ padding: '0px !important' }} key={p.ID} className="card-content">
        {showTitle ? <Typography style={productTitle}>{p.ProductTitle}</Typography> : null}

        {p.BrokerageItemLinks !== null
          ? p.BrokerageItemLinks.map((b, index) => (
              <li key={index}>
                <ArticleIcon sx={{ ...productIconStyles, color: '#2CA6F4' }} />
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    if (b.LinkTypeID === linkTypeQuery.LINKTYPE.FILE) {
                      handleSecuredFile(
                        `${process.env.REACT_APP_AGENTPORTAL_PREFIX}/Brokerage/${b.Link}`
                      );
                    } else {
                      window.open(b.Link, '_blank');
                    }
                  }}
                >
                  {b.LinkName}
                </a>
              </li>
            ))
          : null}
      </List>

      <Box>
        <List sx={{ padding: '0px !important' }} key={p.ID}>
          {p.ContactName != null && p.ContactName.length > 0 ? (
            <li style={productContent}>{p.ContactName}</li>
          ) : null}
          {p.ContactEmail != null && p.ContactEmail.length > 0 ? (
            <li style={productContent}>
              <EmailIcon sx={productIconStyles} />
              {'Email: ' + p.ContactEmail}
            </li>
          ) : null}
          {p.ContactPhone != null && p.ContactPhone.length > 0 && p.ContactWebsite == null ? (
            <li style={productContent}>
              <PhoneIcon sx={productIconStyles} />
              {'Phone: ' + p.ContactPhone}
            </li>
          ) : null}
        </List>
        {p.ContactWebsite != null && p.ContactPhone != null && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: '.5rem',
              padding: '.5rem 0',
            }}
            key={p.ID}
          >
            {p.ContactWebsite != null ? (
              <div>
                <LanguageIcon sx={{ ...productIconStyles, color: '#0366d6' }} />
                <a href={p.ContactWebsite} target="_blank" rel="noreferrer">
                  Company Website
                </a>
              </div>
            ) : null}
            {p.ContactWebsite != null && p.ContactPhone != null && '|'}
            {p.ContactPhone != null ? (
              <div>
                <PhoneIcon sx={productIconStyles} />
                {'Phone: ' + p.ContactPhone}
              </div>
            ) : null}
          </Box>
        )}
      </Box>
    </div>
  );

  const DisplayCategory = (cat, index, categoriesArrLength) => {
    // check to see if we are life insurance and using a dash, if so, don't show the product title
    let isUsingDash = false;
    const isLastIndex = index === categoriesArrLength - 1;

    if (cat.CategoryName.indexOf('Life Insurance -') != -1) {
      isUsingDash = true;
    }

    return (
      <Box
        sx={{
          paddingBottom: isLastIndex || cat.length > 1 ? '' : '.5rem',
          marginBottom: isLastIndex ? '' : '.5rem',
        }}
      >
        {(cat.CategoryName == currentCategory) |
        (currentCategory == 'ALL') |
        (cat.CategoryName == '') ? (
          <div>
            {cat.CategoryName.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  padding: '.3rem 0',
                }}
              >
                <Typography
                  sx={{
                    display: 'inline',
                    backgroundColor: '#008de6',
                    padding: '0.5rem 1rem',
                    color: 'white',
                    textAlign: 'center',
                    width: 'auto',
                    borderRadius: '9999px',
                    fontSize: '.875rem',
                    fontWeight: '500',
                  }}
                  variant="body"
                >
                  {cat.CategoryName}
                </Typography>
              </Box>
            )}
            <Box>{DisplayProduct(cat.Item, !isUsingDash)}</Box>
          </div>
        ) : null}
      </Box>
    );
  };

  const handleCategoryChange = (event) => {
    setCurrentCategory(event.target.value);
    FilterProducts(event.target.value, companies);
  };

  const [currentCategory, setCurrentCategory] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [categories, setCategories] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [isHealthInsurance, setHealthInsurance] = useState(false);
  const [brokerageHeaderItems, setBrokerageHeaderItems] = useState(null);

  const getBrokerageProductsQuery = useQuery({
    queryKey: ['GetBrokerageProducts'],
    queryFn: async () => await GetBrokerageProducts()(),
  });
  const getBrokerageCategoriesQuery = useQuery({
    queryKey: ['GetBrokerageCategories'],
    queryFn: async () => await GetBrokerageCategories()(),
  });
  const getBrokerageHeaderItemsQuery = useQuery({
    queryKey: ['GetBrokerageHeaderItems'],
    queryFn: async () => await GetBrokerageHeaderItems()(),
  });

  // STYLES

  const container = {
    width: '100%',
    height: 'auto',
  };

  const filterStyle = {
    margin: '3em 0',
    padding: '0px !important',
  };

  const productTitle = {
    fontWeight: 'bold',
    color: 'black',
  };

  const productSubTitle = {
    textAlign: 'left',
    fontStyle: 'bold',
    marginTop: '0.5em',
  };

  const productCategory = {
    backgroundColor: '#3b82f6',
    padding: '0.5em 1.75em 0.5em 0.5em',
    color: 'white',
    textAlign: 'center',
    width: 'auto',
    borderRadius: '10px',
  };

  const productContent = {
    paddingBottom: '1em',
    textAlign: 'left',
    textDecoration: 'none',
    listStyle: 'none',
  };

  const healthInsuranceExtraContent = {
    fontWeight: '400',
    border: '0',
    boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
    textAlign: 'left',
  };

  // INIT

  useEffect(() => {
    if (getBrokerageProductsQuery.data != null) {
      const parsedData = JSON.parse(getBrokerageProductsQuery.data);

      setCompanies(parsedData);

      FilterProducts('ALL', parsedData);

      setLoading(false);
    }
  }, [getBrokerageProductsQuery.data]);

  useEffect(() => {
    if (getBrokerageCategoriesQuery.data != null) {
      let parsedData = JSON.parse(getBrokerageCategoriesQuery.data);

      setCategories(parsedData);
      setCurrentCategory(parsedData[parsedData.length - 1].CategoryName);
    }
  }, [getBrokerageCategoriesQuery.data]);

  useEffect(() => {
    if (getBrokerageHeaderItemsQuery.data != null) {
      let parsedData = JSON.parse(getBrokerageHeaderItemsQuery.data);

      setBrokerageHeaderItems(parsedData);
    }
  }, [getBrokerageHeaderItemsQuery.data]);

  // FUNCTIONS

  function FilterProducts(catFilter, data) {
    if (catFilter == 'ALL') {
      setFilteredCompanies(data);
      return;
    }

    const tmpCompanies = [];

    setHealthInsurance(catFilter === 'Health Insurance');

    //find the companies that use the category
    companies.forEach((r) => {
      r.Categories.forEach((c) => {
        if (tmpCompanies.includes(r) == false) {
          //if we don't have the company
          if (c.CategoryName.indexOf(catFilter) >= 0) {
            //and we have the category

            tmpCompanies.push(r);
          }
        }
      });
    });

    setFilteredCompanies(tmpCompanies);
  }

  function productCategories(companyFilter, index) {
    //const tmpProducts = filteredProducts.filter((o) => o.Company === companyFilter.companyItem);
    const categoriesArrLength = companyFilter.companyItem.Categories.length;
    return (
      <div>
        <>
          {companyFilter.companyItem.Categories.map((cat, index) => (
            <React.Fragment key={cat.CategoryName + index}>
              {DisplayCategory(cat, index, categoriesArrLength)}
            </React.Fragment>
          ))}
        </>
      </div>
    );
  }

  function GetBrokerageWindow() {
    return (
      <div className="container-fluid" style={container}>
        {categories !== null && (
          <FormControl style={filterStyle}>
            <InputLabel id="demo-simple-select-label">Available Products</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="category-select"
              value={currentCategory}
              onChange={handleCategoryChange}
            >
              {categories.map((categoryItem, index) => (
                <MenuItem key={index} value={categoryItem.CategoryName}>
                  {categoryItem.CategoryName}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select a Category to View their Products</FormHelperText>
          </FormControl>
        )}

        <Box sx={{ flexGrow: 1, marginTop: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {isHealthInsurance && (
              <Box
                sx={{ padding: '1rem', marginBottom: '1rem' }}
                style={healthInsuranceExtraContent}
              >
                To request a group medical quote, please complete the group census form below:
                <ul
                  className="card-content"
                  style={{ textAlign: 'left', listStyle: 'none', padding: '0' }}
                >
                  <li>
                    <a
                      href="https://baltlife.sharepoint.com/Shared%20Documents/NIM_Group_Quote_Request_Form_8-2017_bl.xlsx"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Group Census Form (xlsx)
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.nimbroker.com/spplanmaterialsfiledownloadBaltLife.php?file=NIM_Group_Quote_Request_Form_8-2017_bl&type=pdf&loc=docs&mode=bl"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Group Census Form (pdf)
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://baltlife.sharepoint.com/Shared%20Documents/NIM_GroupLifeAndDI_CensusForm_bl.doc"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Life & DI Census Form (doc)
                    </a>
                  </li>
                </ul>
              </Box>
            )}
          </Box>
          <XMasonry maxColumns={4} responsive targetBlockWidth={500}>
            {filteredCompanies !== null &&
              filteredCompanies.map((companyItem, index) => (
                <XBlock key={companyItem.CompanyName + companyItem.ID}>
                  <Card>
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#0075BF',
                        textAlign: 'center',
                        padding: '.7rem 0',
                        color: 'white',
                        fontSize: '1.1rem',
                      }}
                    >
                      {companyItem.CompanyName}{' '}
                    </Typography>
                    <Box sx={{ padding: '1rem', borderBottom: 'solid 1px lightgrey' }}>
                      {productCategories({ companyItem }, index)}
                    </Box>
                  </Card>
                </XBlock>
              ))}
          </XMasonry>
        </Box>
      </div>
    );
  }

  return (
    <>
      <div id="header-wrapper">
        <div id="image">
          <img src="/images/Brokerage-Header.jpg" alt="Brokerage-Header" />
        </div>
        <div id="title">
          <h1>Brokerage</h1>
          <p>Atlas Insurance Distributor</p>
        </div>
        <div id="details">
          <h3>Baltimore Life Documents</h3>
          <ul>
            {brokerageHeaderItems !== null &&
              brokerageHeaderItems.map((item) => (
                <li key={item.LinkTitle + item.ID}>
                  <SharepointLink
                    baseUrl={`${process.env.REACT_APP_AGENTPORTAL_PREFIX}/Brokerage`}
                    linkType={item.LinkTypeID}
                    link={item.Link}
                  >
                    {item.LinkTitle}
                  </SharepointLink>
                </li>
              ))}
          </ul>
        </div>
      </div>
      {isLoading || linkTypeQuery.isLoading ? <LoadingIndicator /> : GetBrokerageWindow()}
    </>
  );
}

export { Brokerage };
